// The model tab set, shared by the page strip (`ModelHomePage`) and the side rail's model links so the
// two cannot disagree about which tabs exist, what they are called, or who may see them. A module of its
// own, not an export of the page: `ModelHomePage` is `React.lazy` and the rail is on the boot path.

/**
 * The model homepage's tab strip, EXACTLY as the locked mock declares it.
 *
 * <p>THE MOCK DECLARES FIVE: <code>Overview · Projects · Test Ground · Toolbox · Access</code>,
 * and all five are now drawn. <b>`Access` is LAST, and its position is the ruling R63, not a
 * leftover</b> — the mock moved it there because it is the tab people visit least and used to hit
 * first, when it sat second.</p>
 *
 * <p>⚠️ <b>`Access` is READ-ONLY, and that is R60, not an unfinished half.</b> The mock's own words:
 * <i>"everywhere else in this design a control you cannot use is greyed rather than hidden …
 * Access is the exception."</i> Measured 2026-08-23: the registry has <b>no roster read</b> — both
 * repository access methods are POINT reads keyed <code>(model_id, subject_id)</code>, so nothing
 * in the product can enumerate a model's grants. The tab therefore names what it cannot show
 * instead of drawing an empty table, which would assert "nobody".</p>
 *
 * <p>🔑 <b>THREE OF THESE ARE RE-HOMED SURFACES, NOT NEW ONES.</b> The overhaul shell drops
 * `Projects`, `Reggie` and `Toolbox` from the top nav — the mock's own words, <i>"the first two are
 * tabs on the model homepage, the third is absorbed by Dashboard"</i> — so those components mount
 * HERE. Reuse the module, move its parent; nothing is rebuilt and nothing is stranded.</p>
 */
export const MODEL_TABS = ['overview', 'projects', 'testground', 'toolbox', 'access'] as const;
export type ModelTab = (typeof MODEL_TABS)[number];

/** R63 puts `access` LAST; the rest are the mock's own order. One map, so a tab cannot be drawn
 *  with one label here and reached with another there. */
export const TAB_LABEL_KEY: Record<ModelTab, string> = {
  overview: 'common:modelHome.tabOverview',
  projects: 'common:modelHome.tabProjects',
  testground: 'common:modelHome.tabTestGround',
  toolbox: 'common:modelHome.tabToolbox',
  access: 'common:modelHome.tabAccess',
};

/**
 * The tabs THIS CALLER can see — the denominator for both the strip and the resolver.
 *
 * ➕ 2026-10-26 — OPERATOR RULING: EVERYTHING IS READ-VISIBLE TO EVERY TENANT USER, and write is set
 * per model on the Access tab. Test Ground is therefore in EVERY caller's strip. ~~Until now it
 * was hidden for a caller without `reggie_enabled` (WI-09 denial-by-absence)~~ — that gate decided
 * who could READ, and the ruling moved the line: a caller without write still reads every test
 * workspace, and the write controls inside it are disabled with a stated reason (`can_write` on
 * each workspace row, `caller.may_write` on the model). The signature lost its parameter with the
 * gate; a caller cannot be at a tab that is not in this list, so `resolveTab` below stays the one
 * place a stale `?tab=` is bounced.
 */
export function visibleModelTabs(): readonly ModelTab[] {
  return MODEL_TABS;
}

/** ⚠️ An unknown, absent, or NOT-VISIBLE-TO-THIS-CALLER `?tab=` resolves to Overview rather than to
 *  a blank strip. The three cases are one case: a coordinate this caller cannot be at. */
export function resolveTab(raw: string | undefined): ModelTab {
  return (visibleModelTabs() as readonly string[]).includes(raw ?? '')
    ? (raw as ModelTab)
    : 'overview';
}
